// =============================================================================
// Fase 5.5 — Hook centrale wizard RCA Convenzioni
// State condiviso + docs registry (deduplicazione doc) + submit lead
// =============================================================================
import { useState, useCallback, useMemo } from "react";
import axios from "axios";
import { trackWizardSubmit, trackAccountCreated } from "@/lib/analytics";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// ID univoci dei documenti (dedup registry)
export const DOC_IDS = {
  DOC_ID_DIP: "documento_identita_dipendente",
  BADGE_DIP: "badge_dipendente",
  BUSTA_DIP: "busta_paga_dipendente",
  STATO_FAM: "stato_famiglia",
  DOC_ID_FAM: "documento_identita_stesso_nucleo_familiare",
  LIB_VEIC: "libretto_veicolo_da_assicurare",
  LIB_CLASSE: "libretto_veicolo_classe_merito",
  ATTESTATO: "attestato_rischio_disponibile",
  // FASE A — Cambio Veicolo / Vendita / Rottamazione
  DOC_VENDITA_ROTTAMAZIONE: "documento_vendita_rottamazione",
  LIB_NUOVO_VEICOLO: "libretto_nuovo_veicolo",
  DOC_IBAN: "iban_rimborso",
};

// Regole documenti in base a (soggetto, tipo_richiesta, rc_familiare, intestatario_donante, sub_operazione)
export function computeRequiredDocs({ soggetto, tipo, rcFamiliare, intestatarioDonante, subOperazione }) {
  const req = new Set();
  const opt = new Set();
  if (tipo === "contatto") return { required: [], optional: [] };

  // FASE A — Cambio veicolo / vendita-rottamazione: set di doc dedicato
  if (tipo === "cambio_veicolo") {
    // Doc convenzione (identità + badge) sempre richiesti
    req.add(DOC_IDS.DOC_ID_DIP);
    req.add(DOC_IDS.BADGE_DIP);
    // Documento vendita/rottamazione: obbligatorio in entrambi i sotto-flow
    req.add(DOC_IDS.DOC_VENDITA_ROTTAMAZIONE);
    if (subOperazione === "cambio") {
      req.add(DOC_IDS.LIB_NUOVO_VEICOLO);
    } else if (subOperazione === "vendita_rottamazione") {
      req.add(DOC_IDS.DOC_IBAN);
    }
    return { required: Array.from(req), optional: Array.from(opt) };
  }

  // Doc base convenzione
  if (soggetto === "dipendente") {
    req.add(DOC_IDS.DOC_ID_DIP);
    req.add(DOC_IDS.BADGE_DIP);
    req.add(DOC_IDS.BUSTA_DIP);
  } else if (soggetto === "familiare") {
    req.add(DOC_IDS.STATO_FAM);
    req.add(DOC_IDS.DOC_ID_FAM);
    req.add(DOC_IDS.DOC_ID_DIP);
  }

  // Libretto veicolo da assicurare (rinnovo/fermo/nuova)
  req.add(DOC_IDS.LIB_VEIC);

  // Attestato: opzionale solo per "fermo"
  if (tipo === "fermo") opt.add(DOC_IDS.ATTESTATO);

  // RC Familiare "sì" → aggiungo libretto donante + stato famiglia se serve
  if (tipo === "nuova" && rcFamiliare === "si") {
    req.add(DOC_IDS.LIB_CLASSE);
    if (intestatarioDonante === "familiare" || soggetto === "familiare") {
      req.add(DOC_IDS.STATO_FAM);
    }
  }
  return { required: Array.from(req), optional: Array.from(opt) };
}

export function useRcaWizard({ partner, sede }) {
  // Step interno del wizard RCA (1..7)
  const [step, setStep] = useState(1);
  const [state, setState] = useState({
    soggetto_richiesta: "",              // "dipendente" | "familiare"
    tipo_richiesta: "",                  // "nuova" | "rinnovo" | "fermo" | "contatto" | "cambio_veicolo"
    // FASE A — sub-operazione per cambio_veicolo
    sub_operazione: "",                  // "cambio" | "vendita_rottamazione" | ""
    targa_veicolo_cambiato: "",          // targa veicolo cambiato/venduto/rottamato
    // Familiare titolare (solo se soggetto=familiare, chiesto in step 3 se serve CF per matching)
    dipendente_titolare: { nome: "", cognome: "", codice_fiscale: "", parentela: "" },
    // Veicolo da assicurare
    veicolo: { targa: "", tipo_veicolo: "auto" },
    // RC Familiare (ex Bersani)
    rc_familiare: "",                    // "si" | "no" | "non_so" | ""
    // v1.75.2 — Domanda dedicata (PRIMA di RC Familiare):
    //   "" (non risposto) | "si" (usa CU esistente) | "no" (mai assicurato)
    veicolo_ha_classe_merito: "",
    sinistri_5y_donante: null,            // true | false | null
    intestatario_donante: "",            // "dipendente" | "familiare" | ""
    donante_info: { nome: "", cognome: "", parentela: "", targa: "", tipo_veicolo: "auto" },
    // Contatto (step 6)
    contatto: {
      nome: "", cognome: "", email: "", telefono: "",
      codice_fiscale: "",              // CF del contraente (chiesto UNA volta qui)
      comune_residenza: "", provincia_residenza: "",
      note: "",
    },
    contatto_preferito: "",              // "chiamata" | "whatsapp" | "email"
    contatto_dettagli: { giorno: "", fascia_oraria: "", messaggio: "" },
    // Fase 5.6 — Garanzie di interesse (RCA sempre inclusa, altre opzionali)
    garanzie_interesse: ["rca"],
    // Privacy
    privacy_ok: false,
    trattamento_dati_ok: false,
    marketing_ok: false,
    // FASE K2 — opt-in creazione account cliente (default false, GDPR opt-in esplicito)
    crea_account_ok: false,
    // Edge case
    richiesta_verifica_manuale: false,
  });

  // Docs registry: { [DOC_ID]: { file_id, filename, uploaded_at, status } }
  const [docs, setDocs] = useState({});

  const setField = useCallback((path, value) => {
    setState((s) => {
      const clone = { ...s };
      const parts = path.split(".");
      let cur = clone;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...cur[parts[i]] };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return clone;
    });
  }, []);

  const setDoc = useCallback((docId, payload) => {
    setDocs((d) => ({ ...d, [docId]: { ...(d[docId] || {}), ...payload } }));
  }, []);

  const removeDoc = useCallback((docId, soft = true) => {
    setDocs((d) => {
      const clone = { ...d };
      if (soft && clone[docId]) {
        // soft-remove: mantieni il file_id ma marca "non_richiesto"
        clone[docId] = { ...clone[docId], status: "non_richiesto" };
      } else {
        delete clone[docId];
      }
      return clone;
    });
  }, []);

  // Documenti richiesti al momento
  const requiredDocs = useMemo(
    () => computeRequiredDocs({
      soggetto: state.soggetto_richiesta,
      tipo: state.tipo_richiesta,
      rcFamiliare: state.rc_familiare,
      intestatarioDonante: state.intestatario_donante,
      subOperazione: state.sub_operazione,
    }),
    [state.soggetto_richiesta, state.tipo_richiesta, state.rc_familiare,
      state.intestatario_donante, state.sub_operazione]
  );

  const isDocUploaded = useCallback(
    (docId) => {
      const d = docs[docId];
      return !!(d && d.file_id && d.status !== "non_richiesto");
    },
    [docs]
  );

  const missingRequiredDocs = useMemo(
    () => requiredDocs.required.filter((id) => !isDocUploaded(id)),
    [requiredDocs, isDocUploaded]
  );

  // Total step counter (progress interna wizard) — dinamico in base al percorso
  const totalSteps = useMemo(() => {
    // Fase 5.6 — aggiunto step "Garanzie" prima di Contatto per nuova/rinnovo/fermo
    if (state.tipo_richiesta === "contatto") return 3; // soggetto, tipo, contatto+riepilogo
    if (state.tipo_richiesta === "nuova") return 8;    // + garanzie
    // FASE A — cambio_veicolo
    if (state.tipo_richiesta === "cambio_veicolo") {
      // cambio: soggetto, tipo, sub-bivio, doc-cambio, garanzie, contatto, riepilogo = 7
      // vendita/rottamazione: soggetto, tipo, sub-bivio, doc-vendita, contatto, riepilogo = 6 (no garanzie)
      return state.sub_operazione === "vendita_rottamazione" ? 6 : 7;
    }
    return 7; // rinnovo/fermo: soggetto, tipo, docs, veicolo, garanzie, contatto, riepilogo
  }, [state.tipo_richiesta, state.sub_operazione]);

  const submitLead = useCallback(async () => {
    const payload = {
      partner: partner?.id,
      sede: sede || null,
      leonardo_azienda: null,
      tipo: "auto", // retrocompat backend (auto = rca)
      wizard_version: "rca_v5.5",

      // Nuovi campi Fase 5.5
      soggetto_richiesta: state.soggetto_richiesta,
      tipo_richiesta: state.tipo_richiesta,
      // FASE A — cambio veicolo / vendita
      sub_operazione: state.tipo_richiesta === "cambio_veicolo" ? state.sub_operazione : null,
      targa_veicolo_cambiato: state.tipo_richiesta === "cambio_veicolo"
        ? (state.targa_veicolo_cambiato || "").toUpperCase().replace(/\s/g, "") : null,
      dipendente_titolare: state.soggetto_richiesta === "familiare"
        ? state.dipendente_titolare : null,
      veicolo: state.veicolo,
      rc_familiare: state.rc_familiare || null,
      // v1.75.2 — Domanda dedicata veicolo con CU esistente
      veicolo_ha_classe_merito: state.veicolo_ha_classe_merito || null,
      sinistri_5y_donante: state.sinistri_5y_donante,
      intestatario_donante: state.intestatario_donante || null,
      donante_info: state.rc_familiare === "si" ? state.donante_info : null,
      contatto: state.contatto,
      contatto_preferito: state.contatto_preferito || null,
      contatto_dettagli: state.contatto_dettagli,
      privacy_ok: state.privacy_ok,
      trattamento_dati_ok: state.trattamento_dati_ok,
      marketing_ok: state.marketing_ok,
      // FASE K2 — invio flag opt-in al backend RCA
      crea_account_ok: !!state.crea_account_ok,
      richiesta_verifica_manuale: state.richiesta_verifica_manuale,
      // Fase 5.6 — Garanzie (omesse per percorso "contatto" e "vendita_rottamazione")
      garanzie_interesse: (state.tipo_richiesta === "contatto"
        || (state.tipo_richiesta === "cambio_veicolo" && state.sub_operazione === "vendita_rottamazione"))
        ? null
        : (state.garanzie_interesse && state.garanzie_interesse.length
            ? state.garanzie_interesse
            : ["rca"]),

      // Documenti (dedupli): mappa docId → file_id
      documenti: Object.fromEntries(
        Object.entries(docs)
          .filter(([, v]) => v.file_id && v.status !== "non_richiesto")
          .map(([k, v]) => [k, {
            file_id: v.file_id, filename: v.filename || null,
          }])
      ),

      // Compat campi vecchi (retrocompat)
      nome: state.contatto.nome,
      cognome: state.contatto.cognome,
      email: state.contatto.email || "",
      telefono: state.contatto.telefono,
      privacy_accettata: state.privacy_ok,
      mup_accettato: state.trattamento_dati_ok,
      documenti_dopo: true,
    };
    const res = await axios.post(`${API}/convenzioni/rca-lead`, payload);
    // Analytics tracking
    try {
      trackWizardSubmit({ ramo: "rca", partner_id: partner?.id });
      if (res.data?.account_cliente?.created) {
        trackAccountCreated({ ramo: "rca" });
      }
    } catch { /* ignore */ }
    return res.data;
  }, [partner, sede, state, docs]);

  return {
    step, setStep, totalSteps,
    state, setField,
    docs, setDoc, removeDoc, isDocUploaded,
    requiredDocs, missingRequiredDocs,
    submitLead,
  };
}
